// Geometry for a door opening's parametric TOP PROFILE (flat | chamfer | arc) in the quad editor.
//
// The editor's overlay SVG is viewBox="0 0 100 100" + preserveAspectRatio="none", i.e. an ANISOTROPIC
// scale onto the photo. Straight segments survive that distortion, so chamfer cuts can be drawn
// directly in normalized space — but circles don't (an SVG A/Q command would render as a skewed
// ellipse). Arcs are therefore SAMPLED as a polyline: the true circle is computed in an
// aspect-corrected space (x scaled by the photo's width/height ratio, so distances are proportional
// to real image pixels), sampled, and each sample mapped back to normalized space. This mirrors the
// server's construction: apex = top-edge midpoint + outward normal × rise × quad height, outline =
// the circular arc through the two top corners and that apex.

import type { NormalizedPoint, NormalizedQuad, TopProfile } from "../api/types";

const ARC_SAMPLES = 24;

export const lerpPoint = (a: NormalizedPoint, b: NormalizedPoint, t: number): NormalizedPoint => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

// Aspect space: x is multiplied by the photo aspect (width/height) so Euclidean distance matches the
// real image up to a uniform scale. y is untouched, so mapping back is just x /= aspect.
const toAspect = (p: NormalizedPoint, aspect: number): NormalizedPoint => ({ x: p.x * aspect, y: p.y });
const fromAspect = (p: NormalizedPoint, aspect: number): NormalizedPoint => ({ x: p.x / aspect, y: p.y });

const dist = (a: NormalizedPoint, b: NormalizedPoint): number => Math.hypot(b.x - a.x, b.y - a.y);

// The top edge's local frame in aspect space: midpoint, OUTWARD unit normal (away from the door
// interior, i.e. away from the bottom edge's midpoint) and the quad height (average jamb length —
// the unit `rise` is expressed in).
function topEdgeFrame(q: NormalizedQuad, aspect: number) {
  const tl = toAspect(q.topLeft, aspect);
  const tr = toAspect(q.topRight, aspect);
  const bl = toAspect(q.bottomLeft, aspect);
  const br = toAspect(q.bottomRight, aspect);
  const mid = { x: (tl.x + tr.x) / 2, y: (tl.y + tr.y) / 2 };
  const len = dist(tl, tr) || 1;
  let normal = { x: (tr.y - tl.y) / len, y: -(tr.x - tl.x) / len };
  const inward = { x: (bl.x + br.x) / 2 - mid.x, y: (bl.y + br.y) / 2 - mid.y };
  if (normal.x * inward.x + normal.y * inward.y > 0) normal = { x: -normal.x, y: -normal.y };
  const quadHeight = (dist(tl, bl) + dist(tr, br)) / 2;
  return { tl, tr, mid, normal, quadHeight };
}

/** Apex of the arc profile (normalized space): top-edge midpoint pushed along the outward normal by
 * rise × quad height, computed in aspect space (mirrors the server). */
export function arcApexPoint(q: NormalizedQuad, rise: number, aspect: number): NormalizedPoint {
  const f = topEdgeFrame(q, aspect);
  return fromAspect({ x: f.mid.x + f.normal.x * rise * f.quadHeight, y: f.mid.y + f.normal.y * rise * f.quadHeight }, aspect);
}

/** Outward rise of a point above the quad's top edge as a fraction of quad height (the inverse of
 * arcApexPoint — feeds the midpoint handle's drag); negative when the point is inside the door. */
export function outwardRise(q: NormalizedQuad, p: NormalizedPoint, aspect: number): number {
  const f = topEdgeFrame(q, aspect);
  if (f.quadHeight < 1e-9) return 0;
  const pa = toAspect(p, aspect);
  return ((pa.x - f.mid.x) * f.normal.x + (pa.y - f.mid.y) * f.normal.y) / f.quadHeight;
}

/** Parameter t of a point projected onto the from→to edge, computed in aspect space so t is a true
 * fraction of the edge LENGTH (what the chamfer cut fractions mean). Unclamped — callers clamp. */
export function projectEdgeParam(p: NormalizedPoint, from: NormalizedPoint, to: NormalizedPoint, aspect: number): number {
  const pa = toAspect(p, aspect);
  const a = toAspect(from, aspect);
  const b = toAspect(to, aspect);
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return 0;
  return ((pa.x - a.x) * dx + (pa.y - a.y) * dy) / len2;
}

/**
 * Where the on-photo apex HANDLE should sit: the drawn arc's visually highest point (smallest
 * screen y) is WRONG too — under a tilted chord it degenerates toward the higher corner. What
 * reads as "the center of the door top" is the PERSPECTIVE-CORRECT physical middle (where the
 * carriage doors' center seam is): the quad's diagonals intersect at the projective center of the
 * opening, and casting that point along the mean jamb direction onto the arc lands on the door's
 * true top-middle. On a fronto-parallel quad this reduces exactly to the chord-perpendicular apex.
 * Drag math is unchanged: the handle's POSITION follows the physical middle while the drag still
 * projects onto the chord normal (outwardRise), so grabbing it and pulling adjusts the same rise.
 * (Field reports, 2026-07-12: geometric apex read as off-center; screen-peak read as cornered.)
 */
export function arcApexHandlePoint(q: NormalizedQuad, rise: number, aspect: number): NormalizedPoint {
  const pts = arcTopPolyline(q, rise, aspect);
  const mid = pts[Math.floor(pts.length / 2)]; // parametric middle = geometric apex (fallback)
  if (pts.length < 3) return mid;

  // All projective work happens in aspect-corrected space.
  const A = toAspect(q.topLeft, aspect);
  const B = toAspect(q.topRight, aspect);
  const C = toAspect(q.bottomRight, aspect);
  const D = toAspect(q.bottomLeft, aspect);

  // Diagonal intersection = the projective center of the opening.
  const X = segmentIntersection(A, C, B, D);
  if (!X) return mid;

  // Mean jamb direction, bottom → top (the "vertical" of the door plane in the image).
  let jx = (A.x - D.x) + (B.x - C.x);
  let jy = (A.y - D.y) + (B.y - C.y);
  const jLen = Math.hypot(jx, jy);
  if (jLen < 1e-9) return mid;
  jx /= jLen;
  jy /= jLen;

  // First hit of the ray X + t·jamb (t > 0) against the arc's segments.
  for (let i = 0; i + 1 < pts.length; i++) {
    const p1 = toAspect(pts[i], aspect);
    const p2 = toAspect(pts[i + 1], aspect);
    const hit = raySegment(X, { x: jx, y: jy }, p1, p2);
    if (hit) return fromAspect(hit, aspect);
  }
  return mid;
}

/** Intersection point of segments/lines AB and CD (treated as infinite lines); null when parallel. */
function segmentIntersection(a: NormalizedPoint, b: NormalizedPoint, c: NormalizedPoint, d: NormalizedPoint): NormalizedPoint | null {
  const d1x = b.x - a.x, d1y = b.y - a.y;
  const d2x = d.x - c.x, d2y = d.y - c.y;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((c.x - a.x) * d2y - (c.y - a.y) * d2x) / denom;
  return { x: a.x + d1x * t, y: a.y + d1y * t };
}

/** Intersection of the ray (origin, dir) with segment p1→p2; null when they miss. */
function raySegment(origin: NormalizedPoint, dir: NormalizedPoint, p1: NormalizedPoint, p2: NormalizedPoint): NormalizedPoint | null {
  const sx = p2.x - p1.x, sy = p2.y - p1.y;
  const denom = dir.x * sy - dir.y * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((p1.x - origin.x) * sy - (p1.y - origin.y) * sx) / denom;
  const s = ((p1.x - origin.x) * dir.y - (p1.y - origin.y) * dir.x) / denom;
  if (t <= 0 || s < 0 || s > 1) return null;
  return { x: origin.x + dir.x * t, y: origin.y + dir.y * t };
}

/** The arched top edge as sample points from topLeft to topRight (inclusive), on the circle through
 * (topLeft, apex, topRight). Falls back to the straight edge when the rise is ~0 (collinear). */
export function arcTopPolyline(q: NormalizedQuad, rise: number, aspect: number): NormalizedPoint[] {
  const { tl, tr } = topEdgeFrame(q, aspect);
  const apex = toAspect(arcApexPoint(q, rise, aspect), aspect);
  // Circumcenter of (tl, apex, tr).
  const det = 2 * (tl.x * (apex.y - tr.y) + apex.x * (tr.y - tl.y) + tr.x * (tl.y - apex.y));
  if (Math.abs(det) < 1e-9) return [q.topLeft, q.topRight];
  const a2 = tl.x * tl.x + tl.y * tl.y;
  const p2 = apex.x * apex.x + apex.y * apex.y;
  const b2 = tr.x * tr.x + tr.y * tr.y;
  const c = {
    x: (a2 * (apex.y - tr.y) + p2 * (tr.y - tl.y) + b2 * (tl.y - apex.y)) / det,
    y: (a2 * (tr.x - apex.x) + p2 * (tl.x - tr.x) + b2 * (apex.x - tl.x)) / det,
  };
  const r = dist(c, tl);
  const a0 = Math.atan2(tl.y - c.y, tl.x - c.x);
  const a1 = Math.atan2(tr.y - c.y, tr.x - c.x);
  const ap = Math.atan2(apex.y - c.y, apex.x - c.x);
  // Sweep from tl to tr in whichever rotational direction passes through the apex.
  const TWO_PI = Math.PI * 2;
  const ccw = (from: number, to: number) => (((to - from) % TWO_PI) + TWO_PI) % TWO_PI;
  const sweep = ccw(a0, ap) <= ccw(a0, a1) ? ccw(a0, a1) : ccw(a0, a1) - TWO_PI;
  const pts: NormalizedPoint[] = [];
  for (let i = 0; i <= ARC_SAMPLES; i++) {
    const t = a0 + (sweep * i) / ARC_SAMPLES;
    pts.push(fromAspect({ x: c.x + r * Math.cos(t), y: c.y + r * Math.sin(t) }, aspect));
  }
  return pts;
}

/** Full outline polygon (normalized points, clockwise from the top-left region) for a quad + top
 * profile. Flat/null → the 4 virtual corners; chamfer → straight segments through the cut points
 * (cut fractions are fractions OF EDGE LENGTH, which lerp preserves — no aspect correction needed);
 * arc → the sampled arc polyline + the two bottom corners. */
export function doorOutlinePoints(q: NormalizedQuad, profile: TopProfile | null | undefined, aspect: number): NormalizedPoint[] {
  if (!profile || profile.kind === "flat") return [q.topLeft, q.topRight, q.bottomRight, q.bottomLeft];
  if (profile.kind === "chamfer") {
    return [
      lerpPoint(q.topLeft, q.bottomLeft, profile.cutSideLeft),
      lerpPoint(q.topLeft, q.topRight, profile.cutTopLeft),
      lerpPoint(q.topRight, q.topLeft, profile.cutTopRight),
      lerpPoint(q.topRight, q.bottomRight, profile.cutSideRight),
      q.bottomRight,
      q.bottomLeft,
    ];
  }
  return [...arcTopPolyline(q, profile.rise, aspect), q.bottomRight, q.bottomLeft];
}
